import React from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Grid,
    InputLabel,
    OutlinedInput,
    IconButton,
    Typography,
    Chip,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { IAddUpdateMenuItemRequest } from "../../../../model/admin-portal/request/add-update-menu-request";
import { ICustomPageListResponse } from "../../../../model/admin-portal/response/custom-page-list-response";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface MenuItemFormProps {
    id: string;
    item: IAddUpdateMenuItemRequest;
    itemIndex: number;
    onUpdate: (itemIndex: number, field: string, value: any) => void;
    onRemove: (itemIndex: number) => void;
    customPagesList: ICustomPageListResponse[];
    disabled: boolean;
    expanded: boolean;
    onExpandChange: (itemIndex: number, expanded: boolean) => void;
}

export const MenuItemForm: React.FC<MenuItemFormProps> = ({
    id,
    item,
    itemIndex,
    onUpdate,
    onRemove,
    customPagesList,
    disabled,
    expanded,
    onExpandChange
}) => {
    const isDefault = (item as any).isDefault || false;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id, disabled: isDefault });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginBottom: '16px'
    };

    return (
        <Accordion
            ref={setNodeRef}
            style={style}
            sx={{ mb: 2 }}
            expanded={isDefault ? false : expanded}
            onChange={isDefault ? undefined : (_, isExpanded) => onExpandChange(itemIndex, isExpanded)}
            disabled={isDefault}
        >
            <AccordionSummary
                expandIcon={isDefault ? null : <ExpandMoreIcon />}
                sx={{ cursor: isDefault ? 'default' : 'pointer' }}
            >
                <Box display="flex" alignItems="center" gap={1} width="100%">
                    <Box
                        {...(!isDefault ? attributes : {})}
                        {...(!isDefault ? listeners : {})}
                        sx={{ cursor: isDefault ? 'default' : 'grab', display: 'flex' }}
                    >
                        <DragIndicatorIcon color={isDefault ? "disabled" : "action"} />
                    </Box>
                    <Typography sx={{ flexGrow: 1 }}>
                        {item.title || `Item ${itemIndex + 1}`}
                    </Typography>
                    {isDefault && <Chip label="Padrão" size="small" color="primary" />}
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(itemIndex);
                        }}
                        disabled={disabled || isDefault}
                        size="small"
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2}>
                    {/* Título e Visível */}
                    <Grid item xs={12} md={10}>
                        <InputLabel>Título *</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={item.title || ''}
                            onChange={(e) => onUpdate(itemIndex, 'title', e.target.value)}
                            fullWidth
                            required
                            error={!item.title || item.title.length === 0}
                            placeholder="Digite o título do item..."
                            disabled={disabled || isDefault}
                        />
                    </Grid>
                    <Grid item xs={12} md={2} display="flex" alignItems="flex-end">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={item.visible || false}
                                    onChange={(e) => onUpdate(itemIndex, 'visible', e.target.checked)}
                                    disabled={disabled || isDefault}
                                />
                            }
                            label="Visível"
                        />
                    </Grid>

                    {/* Custom Page Selector */}
                    <Grid item xs={12}>
                        <InputLabel>Página Customizada</InputLabel>
                        <Select
                            size="small"
                            value={item.customPageId || ''}
                            onChange={(e) => onUpdate(itemIndex, 'customPageId', e.target.value || null)}
                            disabled={disabled || isDefault}
                            fullWidth
                        >
                            <MenuItem value="">Nenhuma (usar URL externa)</MenuItem>
                            {customPagesList.map(page => (
                                <MenuItem key={page.id} value={page.id}>
                                    {page.title}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="textSecondary">
                            Selecione uma página customizada ou deixe em branco para usar URL externa
                        </Typography>
                    </Grid>

                    {/* URL Externa (se não tem customPageId) */}
                    {!item.customPageId && (
                        <Grid item xs={12}>
                            <InputLabel>URL Externa *</InputLabel>
                            <OutlinedInput
                                size="small"
                                value={item.externalUrl || ''}
                                onChange={(e) => onUpdate(itemIndex, 'externalUrl', e.target.value)}
                                disabled={disabled || isDefault}
                                placeholder="https://..."
                                fullWidth
                                error={!item.externalUrl || item.externalUrl.length === 0}
                            />
                            <Typography variant="caption" color="textSecondary">
                                Obrigatório quando nenhuma página customizada for selecionada
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
